/**
 * Task #3960 — Shared Zod contracts for call-control actions.
 *
 * Discriminated request DTOs and result DTOs for:
 *   hangup, hold, unhold, park, transfer, bridge, dtmf
 *
 * Rules:
 *   - No `Record<…>` generic, no `any`.
 *   - Every request carries actor, tenant_uuid, call_uuid, optional expected_call_state.
 *   - Action-specific fields (destination / other_uuid / digits / cause) are validated
 *     only in the branch that needs them.
 *   - Preserves the existing CallActionSchema (in call.ts).
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Observable command receipt
// ---------------------------------------------------------------------------

export const CommandStateSchema = z.enum([
  'received',
  'executing',
  'accepted',
  'confirmed',
  'failed',
  'unknown',
  'expired',
]);
export type CommandState = z.infer<typeof CommandStateSchema>;

export const ObservableCommandActionSchema = z.enum([
  'hangup',
  'hold',
  'unhold',
  'park',
  'transfer',
  'bridge',
  'dtmf',
  'attended_transfer_start',
  'attended_transfer_cancel',
  'transfer_with_timeout',
  'originate',
]);
export type ObservableCommandAction = z.infer<typeof ObservableCommandActionSchema>;

export const CommandConfirmationSchema = z.object({
  strategy: z.enum(['event', 'multi_event', 'not_observable']),
  eventNames: z.array(z.string().min(1)).max(4),
}).readonly();
export type CommandConfirmation = z.infer<typeof CommandConfirmationSchema>;

/**
 * Public, sanitized receipt for a mutating FreeSWITCH command.
 *
 * It deliberately excludes raw ESL commands/events, actor identity,
 * destinations, DTMF digits and idempotency hashes. Redis is an operational
 * correlation store with bounded retention, not a permanent legal audit log.
 */
export const CommandReceiptSchema = z.object({
  version: z.literal(1),
  commandId: z.string().uuid(),
  state: CommandStateSchema,
  action: ObservableCommandActionSchema,
  tenantId: z.string().uuid(),
  callUuid: z.string().uuid(),
  relatedCallUuids: z.array(z.string().uuid()).max(4).optional(),
  confirmation: CommandConfirmationSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  acceptedAt: z.string().datetime().optional(),
  confirmedAt: z.string().datetime().optional(),
  failedAt: z.string().datetime().optional(),
  confirmationDeadlineAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  errorCode: z.string().min(1).max(64).optional(),
  errorMessage: z.string().min(1).max(256).optional(),
}).readonly();
export type CommandReceipt = z.infer<typeof CommandReceiptSchema>;

// ---------------------------------------------------------------------------
// Shared base — fields present in every call-control request
// ---------------------------------------------------------------------------

const CallControlBaseSchema = z.object({
  /** UUID of the user / agent performing the action */
  actor_uuid: z.string().uuid(),
  /** Tenant scope */
  tenant_uuid: z.string().uuid(),
  /** Target call leg */
  call_uuid: z.string().uuid(),
  /** Optional optimistic-concurrency guard: the client's view of current state */
  expected_call_state: z
    .enum(['ringing', 'answered', 'bridged', 'hold', 'on_hold', 'transfer', 'conference', 'parked'])
    .optional(),
});

// ---------------------------------------------------------------------------
// Request discriminated union
// ---------------------------------------------------------------------------

export const HangupRequestSchema = CallControlBaseSchema.extend({
  action: z.literal('hangup'),
  /** SIP cause or textual reason (e.g. "user_request", "busy", "no_answer") */
  cause: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_-]+$/i)
    .optional(),
});
export type HangupRequest = z.infer<typeof HangupRequestSchema>;

export const HoldRequestSchema = CallControlBaseSchema.extend({
  action: z.literal('hold'),
});
export type HoldRequest = z.infer<typeof HoldRequestSchema>;

export const UnholdRequestSchema = CallControlBaseSchema.extend({
  action: z.literal('unhold'),
});
export type UnholdRequest = z.infer<typeof UnholdRequestSchema>;

export const ParkRequestSchema = CallControlBaseSchema.extend({
  action: z.literal('park'),
});
export type ParkRequest = z.infer<typeof ParkRequestSchema>;

/**
 * Transfer destination — content contract (ESL command sanitisation).
 *
 * Only the dialled target travels in the request: an internal extension, a
 * dialable number (`+` prefix allowed) or one of the backend-owned named
 * dialplan entries used by the ESL IVR handler (`queue_<ext>`, `ivr_<ext>`,
 * `voicemail_<ext>`). The `XML <context>` part is NEVER accepted from the
 * caller: call-control composes it from the authenticated tenant's
 * `sip_domain`. Spaces, quotes, braces, commas and any other character that
 * could re-shape the `uuid_transfer` command are rejected at the boundary.
 */
export const TRANSFER_DESTINATION_PATTERN =
  /^(?:\+?[0-9*#]{2,20}|(?:queue|ivr|voicemail)_[0-9A-Za-z_-]{1,40})$/;

export const TransferDestinationSchema = z
  .string()
  .min(2)
  .max(50)
  .regex(TRANSFER_DESTINATION_PATTERN, 'Invalid transfer destination');

export const TransferRequestSchema = CallControlBaseSchema.extend({
  action: z.literal('transfer'),
  /** Extension / number / named dialplan entry; context is server-derived. */
  destination: TransferDestinationSchema,
});
export type TransferRequest = z.infer<typeof TransferRequestSchema>;

export const BridgeRequestSchema = CallControlBaseSchema.extend({
  action: z.literal('bridge'),
  /** UUID of the other call leg to bridge with */
  other_call_uuid: z.string().uuid(),
});
export type BridgeRequest = z.infer<typeof BridgeRequestSchema>;

export const DtmfRequestSchema = CallControlBaseSchema.extend({
  action: z.literal('dtmf'),
  /** DTMF digit string — only 0-9, *, #, A-D */
  digits: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[0-9*#A-Da-d]+$/),
  duration_ms: z.number().int().min(40).max(2000).default(100),
});
export type DtmfRequest = z.infer<typeof DtmfRequestSchema>;

/** Discriminated union of all call-control request DTOs */
export const CallControlRequestSchema = z.discriminatedUnion('action', [
  HangupRequestSchema,
  HoldRequestSchema,
  UnholdRequestSchema,
  ParkRequestSchema,
  TransferRequestSchema,
  BridgeRequestSchema,
  DtmfRequestSchema,
]);
export type CallControlRequest = z.infer<typeof CallControlRequestSchema>;

// ---------------------------------------------------------------------------
// Result discriminated union
// ---------------------------------------------------------------------------

/** Fields shared by every successful result */
const CallControlResultBaseSchema = z.object({
  success: z.literal(true),
  /** Echoes the action from the originating request */
  action: z.enum(['hangup', 'hold', 'unhold', 'park', 'transfer', 'bridge', 'dtmf']),
  /** ISO-8601 timestamp of when the action was applied */
  applied_at: z.string().datetime(),
  /** Call UUID the action was applied to */
  call_uuid: z.string().uuid(),
  /** Tenant scope */
  tenant_uuid: z.string().uuid(),
});

/** Fields shared by every failed result */
const CallControlErrorBaseSchema = z.object({
  success: z.literal(false),
  action: z.enum(['hangup', 'hold', 'unhold', 'park', 'transfer', 'bridge', 'dtmf']),
  call_uuid: z.string().uuid(),
  tenant_uuid: z.string().uuid(),
  error_code: z.string().min(1).max(64),
  error_message: z.string().max(512),
});

// --- per-action success results ---

export const HangupResultSchema = CallControlResultBaseSchema.extend({
  action: z.literal('hangup'),
  /** Final SIP cause returned by the media layer */
  cause: z.string().min(1).max(64).optional(),
});
export type HangupResult = z.infer<typeof HangupResultSchema>;

export const HoldResultSchema = CallControlResultBaseSchema.extend({
  action: z.literal('hold'),
});
export type HoldResult = z.infer<typeof HoldResultSchema>;

export const UnholdResultSchema = CallControlResultBaseSchema.extend({
  action: z.literal('unhold'),
});
export type UnholdResult = z.infer<typeof UnholdResultSchema>;

export const ParkResultSchema = CallControlResultBaseSchema.extend({
  action: z.literal('park'),
});
export type ParkResult = z.infer<typeof ParkResultSchema>;

export const TransferResultSchema = CallControlResultBaseSchema.extend({
  action: z.literal('transfer'),
  destination: z.string().min(1).max(64),
});
export type TransferResult = z.infer<typeof TransferResultSchema>;

export const BridgeResultSchema = CallControlResultBaseSchema.extend({
  action: z.literal('bridge'),
  other_call_uuid: z.string().uuid(),
});
export type BridgeResult = z.infer<typeof BridgeResultSchema>;

export const DtmfResultSchema = CallControlResultBaseSchema.extend({
  action: z.literal('dtmf'),
  digits: z.string().min(1).max(32).regex(/^[0-9*#A-Da-d]+$/),
  duration_ms: z.number().int().min(40).max(2000),
});
export type DtmfResult = z.infer<typeof DtmfResultSchema>;

/** Discriminated union of all successful call-control result DTOs */
export const CallControlSuccessResultSchema = z.discriminatedUnion('action', [
  HangupResultSchema,
  HoldResultSchema,
  UnholdResultSchema,
  ParkResultSchema,
  TransferResultSchema,
  BridgeResultSchema,
  DtmfResultSchema,
]);
export type CallControlSuccessResult = z.infer<typeof CallControlSuccessResultSchema>;

// --- error result (same shape for all actions) ---

export const CallControlErrorResultSchema = CallControlErrorBaseSchema;
export type CallControlErrorResult = z.infer<typeof CallControlErrorResultSchema>;

/** Union of success + error result */
export const CallControlResultSchema = z.union([
  CallControlSuccessResultSchema,
  CallControlErrorResultSchema,
]);
export type CallControlResult = z.infer<typeof CallControlResultSchema>;

export const ObservedCallControlResultSchema = z.intersection(
  CallControlResultSchema,
  z.object({
    commandId: z.string().uuid(),
    state: CommandStateSchema,
    receipt: CommandReceiptSchema,
  }),
);
export type ObservedCallControlResult = z.infer<typeof ObservedCallControlResultSchema>;
